// URL <-> state helpers for the Variations & SOWs tab. Both sections'
// filter selections + search drafts round-trip through the querystring
// so deep links and back-button navigation preserve user context. Keys
// are scoped per section so the two filter sets cannot stomp each other.
//
// The `?varies={id}` form from the brief is accepted as a deep-link
// **synonym** for `?var_filters[varies]={id}` — the first call to
// `serialise` after a user-driven filter change rewrites it to the
// canonical key, so the brief's URL shape works for incoming links and
// the canonical form takes over from then on.
//
// Defaults are stripped from the URL — `?` and `?<key>=<default>` look
// the same to the user. Mirrors `deliverableUrlState` and
// `milestoneUrlState`.

import type { SowStatus, VariationStatus } from "../../../../types";
import type {
  SowDateRangeFilter,
  SowFilters,
} from "./applySowFilters";
import type {
  VariationDateRangeFilter,
  VariationFilters,
  VariationTypeFilter,
  VariesToken,
} from "./applyVariationFilters";

const SOW_STATUS_VALUES = new Set<SowStatus>([
  "draft",
  "active",
  "completed",
  "cancelled",
]);
const SOW_DATE_VALUES = new Set<SowDateRangeFilter>([
  "next-30-days",
  "past-30-days",
  "this-quarter",
  "all-time",
]);
const VAR_STATUS_VALUES = new Set<VariationStatus>([
  "proposed",
  "approved",
  "rejected",
]);
const VAR_TYPE_VALUES = new Set<VariationTypeFilter>([
  "financial",
  "non-financial",
]);
const VAR_DATE_VALUES = new Set<VariationDateRangeFilter>([
  "next-30-days",
  "past-30-days",
  "this-quarter",
  "all-time",
]);

export interface VariationSowUrlState {
  sowFilters: SowFilters;
  sowSearch: string;
  variationFilters: VariationFilters;
  variationSearch: string;
}

const OWNED_KEYS = [
  "sow_filters[status]",
  "sow_filters[date]",
  "sow_filters[archived]",
  "sow_q",
  "var_filters[status]",
  "var_filters[varies]",
  "var_filters[type]",
  "var_filters[date]",
  "var_filters[archived]",
  "var_q",
  // Brief-form synonym; folded into `var_filters[varies]` on parse and
  // stripped on serialise so the canonical form takes over.
  "varies",
];

function parseList<T extends string>(
  raw: string | null,
  allowed: Set<T>,
): T[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is T => (allowed as Set<string>).has(s));
}

function parseSingle<T extends string>(
  raw: string | null,
  allowed: Set<T>,
  fallback: T,
): T {
  if (!raw) return fallback;
  return (allowed as Set<string>).has(raw) ? (raw as T) : fallback;
}

function parseVariesList(
  raw: string | null,
  legacy: string | null,
): VariesToken[] {
  const out = new Set<VariesToken>();
  const merged = `${raw ?? ""},${legacy ?? ""}`;
  for (const piece of merged.split(",")) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    out.add(trimmed);
  }
  return Array.from(out);
}

export function parseVariationSowUrlState(
  params: URLSearchParams,
): VariationSowUrlState {
  return {
    sowFilters: {
      status: parseList(params.get("sow_filters[status]"), SOW_STATUS_VALUES),
      dateRange: parseSingle(
        params.get("sow_filters[date]"),
        SOW_DATE_VALUES,
        "all-time",
      ),
      includeArchived: params.get("sow_filters[archived]") === "1",
    },
    sowSearch: params.get("sow_q") ?? "",
    variationFilters: {
      status: parseList(params.get("var_filters[status]"), VAR_STATUS_VALUES),
      varies: parseVariesList(
        params.get("var_filters[varies]"),
        params.get("varies"),
      ),
      type: parseList(params.get("var_filters[type]"), VAR_TYPE_VALUES),
      dateRange: parseSingle(
        params.get("var_filters[date]"),
        VAR_DATE_VALUES,
        "all-time",
      ),
      includeArchived: params.get("var_filters[archived]") === "1",
    },
    variationSearch: params.get("var_q") ?? "",
  };
}

export function serialiseVariationSowUrlState(
  state: VariationSowUrlState,
  base?: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(base?.toString() ?? "");
  for (const k of OWNED_KEYS) next.delete(k);

  if (state.sowFilters.status.length) {
    next.set("sow_filters[status]", state.sowFilters.status.join(","));
  }
  if (state.sowFilters.dateRange !== "all-time") {
    next.set("sow_filters[date]", state.sowFilters.dateRange);
  }
  if (state.sowFilters.includeArchived) {
    next.set("sow_filters[archived]", "1");
  }
  if (state.sowSearch.trim()) next.set("sow_q", state.sowSearch);

  if (state.variationFilters.status.length) {
    next.set(
      "var_filters[status]",
      state.variationFilters.status.join(","),
    );
  }
  if (state.variationFilters.varies.length) {
    next.set(
      "var_filters[varies]",
      state.variationFilters.varies.join(","),
    );
  }
  if (state.variationFilters.type.length) {
    next.set("var_filters[type]", state.variationFilters.type.join(","));
  }
  if (state.variationFilters.dateRange !== "all-time") {
    next.set("var_filters[date]", state.variationFilters.dateRange);
  }
  if (state.variationFilters.includeArchived) {
    next.set("var_filters[archived]", "1");
  }
  if (state.variationSearch.trim()) {
    next.set("var_q", state.variationSearch);
  }
  return next;
}

export function emptyVariationSowUrlState(): VariationSowUrlState {
  return {
    sowFilters: {
      status: [],
      dateRange: "all-time",
      includeArchived: false,
    },
    sowSearch: "",
    variationFilters: {
      status: [],
      varies: [],
      type: [],
      dateRange: "all-time",
      includeArchived: false,
    },
    variationSearch: "",
  };
}
